/**
 * Minimal decoder for protobuf-encoded `foxglove.PointCloud` messages.
 *
 * The Foxglove SDK's default recording path writes protobuf, so without this a
 * cloud recorded with the SDK is unreadable here. As with
 * `foxgloveCompressedVideo.ts`, one fixed schema is hand-decoded rather than
 * pulling in a protobuf runtime.
 *
 * The output has the same shape the ROS decoders produce for
 * `foxglove_msgs/PointCloud`, so everything downstream is encoding-agnostic.
 *
 * Schema (https://docs.foxglove.dev/docs/visualization/message-schemas/point-cloud):
 *   field 1: timestamp (message: 1 = sec varint, 2 = nsec varint)
 *   field 2: frame_id (string)
 *   field 3: pose (message: 1 = position Vector3, 2 = orientation Quaternion;
 *            Vector3 is 1 x, 2 y, 3 z and Quaternion 1 x, 2 y, 3 z, 4 w, all doubles)
 *   field 4: point_stride (fixed32)
 *   field 5: fields (repeated message: 1 = name string, 2 = offset fixed32,
 *            3 = type enum varint)
 *   field 6: data (bytes)
 */

import { decodeTimestamp, WireReader, WireType } from "./protobufWire";

export const FOXGLOVE_POINT_CLOUD_SCHEMA_NAME = "foxglove.PointCloud";

export interface FoxglovePackedElementField {
  name: string;
  /** Byte offset from the start of each point. */
  offset: number;
  /** A `foxglove.NumericType` value. */
  type: number;
}

export interface FoxglovePointCloud {
  timestamp: { sec: number; nsec: number };
  frame_id: string;
  pose: {
    position: { x: number; y: number; z: number };
    orientation: { x: number; y: number; z: number; w: number };
  };
  point_stride: number;
  fields: FoxglovePackedElementField[];
  /** A view of the input, not a copy; the caller decodes it right away. */
  data: Uint8Array;
}

const textDecoder = new TextDecoder();

/** Decodes a message whose numbered fields are all doubles into `into`. */
function decodeDoubles(
  data: Uint8Array,
  into: Record<string, number>,
  byFieldNumber: string[],
): void {
  const reader = new WireReader(data);
  while (!reader.atEnd) {
    const tag = reader.varint();
    const fieldNumber = tag >>> 3;
    const wireType = tag & 0x07;
    const key = byFieldNumber[fieldNumber - 1];
    if (key !== undefined && wireType === WireType.Fixed64) {
      into[key] = reader.double();
    } else {
      reader.skip(wireType);
    }
  }
}

function decodePose(data: Uint8Array): FoxglovePointCloud["pose"] {
  const position = { x: 0, y: 0, z: 0 };
  // Identity until an orientation message says otherwise; a present message
  // starts from zeros, as proto3 does, and `w` is then whatever was written.
  let orientation = { x: 0, y: 0, z: 0, w: 1 };
  const reader = new WireReader(data);
  while (!reader.atEnd) {
    const tag = reader.varint();
    const fieldNumber = tag >>> 3;
    const wireType = tag & 0x07;
    if (wireType !== WireType.LengthDelimited) {
      reader.skip(wireType);
      continue;
    }
    const value = reader.bytes(reader.varint());
    if (fieldNumber === 1) {
      decodeDoubles(value, position, ["x", "y", "z"]);
    } else if (fieldNumber === 2) {
      orientation = { x: 0, y: 0, z: 0, w: 0 };
      decodeDoubles(value, orientation, ["x", "y", "z", "w"]);
    }
  }
  return { position, orientation };
}

function decodeField(data: Uint8Array): FoxglovePackedElementField {
  const field: FoxglovePackedElementField = { name: "", offset: 0, type: 0 };
  const reader = new WireReader(data);
  while (!reader.atEnd) {
    const tag = reader.varint();
    const fieldNumber = tag >>> 3;
    const wireType = tag & 0x07;
    if (fieldNumber === 1 && wireType === WireType.LengthDelimited) {
      field.name = textDecoder.decode(reader.bytes(reader.varint()));
    } else if (fieldNumber === 2 && wireType === WireType.Fixed32) {
      field.offset = reader.fixed32();
    } else if (fieldNumber === 3 && wireType === WireType.Varint) {
      field.type = reader.varint();
    } else {
      reader.skip(wireType);
    }
  }
  return field;
}

export function decodePointCloudProtobuf(
  view: ArrayBufferView,
): FoxglovePointCloud {
  const reader = new WireReader(
    new Uint8Array(view.buffer, view.byteOffset, view.byteLength),
  );
  const message: FoxglovePointCloud = {
    timestamp: { sec: 0, nsec: 0 },
    frame_id: "",
    pose: {
      position: { x: 0, y: 0, z: 0 },
      orientation: { x: 0, y: 0, z: 0, w: 1 },
    },
    point_stride: 0,
    fields: [],
    data: new Uint8Array(),
  };
  while (!reader.atEnd) {
    const tag = reader.varint();
    const fieldNumber = tag >>> 3;
    const wireType = tag & 0x07;
    if (fieldNumber === 4 && wireType === WireType.Fixed32) {
      message.point_stride = reader.fixed32();
      continue;
    }
    if (wireType !== WireType.LengthDelimited) {
      reader.skip(wireType);
      continue;
    }
    const value = reader.bytes(reader.varint());
    switch (fieldNumber) {
      case 1:
        message.timestamp = decodeTimestamp(value);
        break;
      case 2:
        message.frame_id = textDecoder.decode(value);
        break;
      case 3:
        message.pose = decodePose(value);
        break;
      case 5:
        message.fields.push(decodeField(value));
        break;
      case 6:
        message.data = value;
        break;
      default:
        break;
    }
  }
  return message;
}
